import type googleMapReact from 'google-map-react';
import type Supercluster from 'supercluster';
import type { ChargingLocationFilters } from '../../utils/api';
import type { OpeningHoursDay, OpeningHoursInfo } from './interface';

type StationPointFeature = Supercluster.PointFeature<Supercluster.AnyProps>;

enum Location {
    Romania = 'Romania',
    Bucharest = 'Bucharest'
}

export const MAP_LOCATIONS: Record<Location, googleMapReact.Coords> = {
    [Location.Romania]: {
        lat: 45.9431,
        lng: 24.9667
    },
    [Location.Bucharest]: {
        lat: 44.4268,
        lng: 26.1025
    }
};

export const GOOGLE_MAP_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_DYNAMIC_MAP_API_KEY ?? '';
export const EARTH_RADIUS_KM = 6371;
export const SIDE_LIST_LIMIT = 4;
export const BUCHAREST_FALLBACK_ZOOM = 14;

export const CITY_LOCATION_TYPES = new Set([
    'locality',
    'administrative_area_level_1',
    'administrative_area_level_2',
    'administrative_area_level_3',
    'postal_town'
]);

export const ADDRESS_LOCATION_TYPES = new Set([
    'street_address',
    'route',
    'street_number',
    'premise',
    'subpremise',
    'intersection',
    'point_of_interest',
    'establishment'
]);

export const getSearchZoomForTypes = (types: string[] = []) => {
    if (types.some((type) => CITY_LOCATION_TYPES.has(type))) {
        return 13;
    }

    if (types.some((type) => ADDRESS_LOCATION_TYPES.has(type))) {
        return 14;
    }

    return 14;
};

const toRadians = (value: number) => (value * Math.PI) / 180;

export const getDistanceKm = (origin: googleMapReact.Coords, station: any) => {
    const [lng, lat] = station.geometry?.coordinates ?? [];
    if (typeof lat !== 'number' || typeof lng !== 'number') return Number.POSITIVE_INFINITY;

    const dLat = toRadians(lat - origin.lat);
    const dLng = toRadians(lng - origin.lng);
    const lat1 = toRadians(origin.lat);
    const lat2 = toRadians(lat);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS_KM * c;
};

export const getDistanceReferenceContext = ({
    searchPinLocation,
    userLocation,
    fallbackPoint,
}: {
    searchPinLocation: googleMapReact.Coords | null;
    userLocation: googleMapReact.Coords | null;
    fallbackPoint: googleMapReact.Coords;
}) => {
    if (searchPinLocation) {
        return {
            label: 'Distanța față de locația căutată',
            point: searchPinLocation,
        };
    }

    if (userLocation) {
        return {
            label: 'Distanța față de locația ta',
            point: userLocation,
        };
    }

    return {
        label: 'Distanța față de centrul Bucureștiului',
        point: fallbackPoint,
    };
};

export const getDistanceLabel = ({
    station,
    searchPinLocation,
    userLocation,
    fallbackPoint,
}: {
    station: any;
    searchPinLocation: googleMapReact.Coords | null;
    userLocation: googleMapReact.Coords | null;
    fallbackPoint: googleMapReact.Coords;
}) => {
    const referenceContext = getDistanceReferenceContext({
        searchPinLocation,
        userLocation,
        fallbackPoint,
    });

    if (!referenceContext.point) return '-';

    const distanceKm = getDistanceKm(referenceContext.point, station);

    if (!Number.isFinite(distanceKm)) return '-';

    if (distanceKm < 1) {
        return `${Math.round(distanceKm * 1000)} m`;
    }
    return `${distanceKm.toFixed(1)} Km`;
};

export const getStatusClassName = (
    status: string,
    classes: {
        available: string;
        busy: string;
        default: string;
    }
) => {
    switch (status) {
        case 'available':
            return classes.available;
        case 'busy':
            return classes.busy;
        default:
            return classes.default;
    }
};

export const getConnectorIcon = (
    connectorType: string,
    icons: {
        type2: { src: string };
        ccs: { src: string };
        chademo: { src: string };
    }
) => {
    switch (connectorType) {
        case 'Type 2':
            return icons.type2;
        case 'CCS':
            return icons.ccs;
        case 'CHAdeMO':
            return icons.chademo;
        default:
            return null;
    }
};

export const getGoogleMapsDirectionsUrl = (
    station: any,
    routeOrigin: googleMapReact.Coords | null
) => {
    const [lng, lat] = station.geometry?.coordinates ?? [];
    if (typeof lat !== 'number' || typeof lng !== 'number') return null;

    const destination = `${lat},${lng}`;
    const params = new URLSearchParams({
        api: '1',
        destination,
        travelmode: 'driving',
    });

    if (routeOrigin) {
        params.set('origin', `${routeOrigin.lat},${routeOrigin.lng}`);
    }

    return `https://www.google.com/maps/dir/?${params.toString()}`;
};

export const applyViewport = ({
    nextCenter,
    nextZoom,
    setCenter,
    setZoom,
    map,
}: {
    nextCenter: googleMapReact.Coords;
    nextZoom: number;
    setCenter: React.Dispatch<React.SetStateAction<googleMapReact.Coords>>;
    setZoom: React.Dispatch<React.SetStateAction<number>>;
    map?: google.maps.Map;
}) => {
    setCenter(nextCenter);
    setZoom(nextZoom);

    if (map) {
        map.panTo(nextCenter);
        map.setZoom(nextZoom);
    }
};

export const initializeMap = ({
    map,
    fallbackCenter,
    fallbackZoom,
    setCenter,
    setZoom,
    setBounds,
    setUserLocation,
    setIsInitialViewportResolved,
}: {
    map: google.maps.Map;
    fallbackCenter: googleMapReact.Coords;
    fallbackZoom: number;
    setCenter: React.Dispatch<React.SetStateAction<googleMapReact.Coords>>;
    setZoom: React.Dispatch<React.SetStateAction<number>>;
    setBounds: React.Dispatch<React.SetStateAction<[number, number, number, number] | undefined>>;
    setUserLocation: React.Dispatch<React.SetStateAction<googleMapReact.Coords | null>>;
    setIsInitialViewportResolved: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const setViewport = (nextCenter: googleMapReact.Coords, nextZoom: number) => {
        applyViewport({
            nextCenter,
            nextZoom,
            setCenter,
            setZoom,
            map,
        });

        const nextBounds = map.getBounds();
        if (nextBounds) {
            const sw = nextBounds.getSouthWest();
            const ne = nextBounds.getNorthEast();
            setBounds([sw.lng(), sw.lat(), ne.lng(), ne.lat()]);
        } else {
            setBounds([
                nextCenter.lng - 1,
                nextCenter.lat - 1,
                nextCenter.lng + 1,
                nextCenter.lat + 1
            ]);
        }

        setIsInitialViewportResolved(true);
    };

    if (!navigator?.geolocation) {
        setViewport(fallbackCenter, fallbackZoom);
        return;
    }

    const geolocationTimeout = window.setTimeout(() => {
        setViewport(fallbackCenter, fallbackZoom);
    }, 10500);

    navigator.geolocation.getCurrentPosition(
        ({coords}) => {
            window.clearTimeout(geolocationTimeout);
            if (coords) {
                const location = {lat: coords.latitude, lng: coords.longitude};
                setUserLocation(location);
                setViewport(location, 13);
                return;
            }

            setViewport(fallbackCenter, fallbackZoom);
        },
        (error) => {
            window.clearTimeout(geolocationTimeout);
            console.warn('Geolocation failed:', error.code, error.message);
            setViewport(fallbackCenter, fallbackZoom);
        },
        {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
        }
    );
};

export const drawRouteToStation = ({
    destination,
    origin,
    maps,
    map,
    directionsService,
    directionsRenderer,
}: {
    destination: {lat: number; lng: number};
    origin: googleMapReact.Coords;
    maps: typeof google.maps | undefined;
    map: google.maps.Map | undefined;
    directionsService: google.maps.DirectionsService | null;
    directionsRenderer: google.maps.DirectionsRenderer | null;
}) => {
    if (!maps || !map) {
        return {
            directionsService,
            directionsRenderer,
        };
    }

    const nextDirectionsService = directionsService ?? new maps.DirectionsService();
    const nextDirectionsRenderer =
        directionsRenderer ??
        new maps.DirectionsRenderer({
            suppressMarkers: false,
            preserveViewport: true,
            polylineOptions: {
                strokeColor: '#0053D6',
                strokeOpacity: 0.9,
                strokeWeight: 5
            }
        });

    if (!directionsRenderer) {
        nextDirectionsRenderer.setMap(map);
    }

    nextDirectionsService.route(
        {
            origin,
            destination,
            travelMode: maps.TravelMode.DRIVING
        },
        (result, status) => {
            if (result && status === maps.DirectionsStatus.OK) {
                nextDirectionsRenderer.setDirections(result);
                return;
            }

            console.warn('Directions failed:', status);
        }
    );

    return {
        directionsService: nextDirectionsService,
        directionsRenderer: nextDirectionsRenderer,
    };
};

export const clearRoute = (directionsRenderer: google.maps.DirectionsRenderer | null) => {
    directionsRenderer?.set('directions', null);
};

export const handleClusterZoom = ({
    cluster,
    zoom,
    maxZoom,
    setCenter,
    setZoom,
    map,
}: {
    cluster: any;
    zoom: number;
    maxZoom: number;
    setCenter: React.Dispatch<React.SetStateAction<googleMapReact.Coords>>;
    setZoom: React.Dispatch<React.SetStateAction<number>>;
    map?: google.maps.Map;
}) => {
    if (!cluster) return;

    const nextCenter = {
        lng: cluster.geometry.coordinates[0],
        lat: cluster.geometry.coordinates[1]
    };
    const nextZoom = Math.min(zoom + 2, maxZoom);

    applyViewport({
        nextCenter,
        nextZoom,
        setCenter,
        setZoom,
        map,
    });
};

export const handleMapChange = ({
    event,
    map,
    defaultCenter,
    setBounds,
    setZoom,
    setCenter,
}: {
    event: googleMapReact.ChangeEventValue;
    map?: google.maps.Map;
    defaultCenter: googleMapReact.Coords;
    setBounds: React.Dispatch<React.SetStateAction<[number, number, number, number] | undefined>>;
    setZoom: React.Dispatch<React.SetStateAction<number>>;
    setCenter: React.Dispatch<React.SetStateAction<googleMapReact.Coords>>;
}) => {
    if (!map) return;

    const centerObj = map.getCenter();

    setBounds([event.bounds.sw.lng, event.bounds.sw.lat, event.bounds.ne.lng, event.bounds.ne.lat]);
    setZoom(event.zoom);

    if (centerObj) {
        setCenter({lat: centerObj.lat(), lng: centerObj.lng()});
        return;
    }

    setCenter(defaultCenter);
};

export const recenterOnMarker = ({
    openMarker,
    stations,
    setCenter,
    setZoom,
    map,
}: {
    openMarker: string | number | null;
    stations: any[];
    setCenter: React.Dispatch<React.SetStateAction<googleMapReact.Coords>>;
    setZoom: React.Dispatch<React.SetStateAction<number>>;
    map?: google.maps.Map;
}) => {
    if (!openMarker) return;
    const openPoint = stations.find((point) => point.properties.id === openMarker);
    if (!openPoint) return;

    applyViewport({
        nextCenter: {
            lng: openPoint.geometry.coordinates[0],
            lat: openPoint.geometry.coordinates[1]
        },
        nextZoom: 13,
        setCenter,
        setZoom,
        map,
    });
};

export const getFilterPayload = ({
    showAvailability,
    showStandard,
    showRapid,
    showType2,
    showCcsCombo2,
    showChademo,
}: {
    showAvailability: boolean;
    showStandard: boolean;
    showRapid: boolean;
    showType2: boolean;
    showCcsCombo2: boolean;
    showChademo: boolean;
}): ChargingLocationFilters => ({
    availability: showAvailability,
    standard: showStandard,
    rapid: showRapid,
    type2: showType2,
    ccsCombo2: showCcsCombo2,
    chademo: showChademo
});

export const getFilterPayloadWithOverrides = (
    baseFilters: ChargingLocationFilters,
    overrides: Partial<ChargingLocationFilters>
): ChargingLocationFilters => ({
    ...baseFilters,
    ...overrides,
});

export const getMapBoundsSnapshot = (
    map: google.maps.Map,
    fallbackCenter: googleMapReact.Coords
): [number, number, number, number] => {
    const nextBounds = map.getBounds();

    if (nextBounds) {
        const sw = nextBounds.getSouthWest();
        const ne = nextBounds.getNorthEast();
        return [sw.lng(), sw.lat(), ne.lng(), ne.lat()];
    }

    return [
        fallbackCenter.lng - 1,
        fallbackCenter.lat - 1,
        fallbackCenter.lng + 1,
        fallbackCenter.lat + 1
    ];
};

export const addOffsetToDuplicateCoordinates = (
    points: StationPointFeature[],
    offset: number
): StationPointFeature[] => {
    const seenCoordinates = new Set<string>();

    return points.map((point, index) => {
        const coordinatesString = point.geometry.coordinates.join(',');

        if (seenCoordinates.has(coordinatesString) && index > 0) {
            const [originalLng, originalLat] = point.geometry.coordinates;
            const newLng = originalLng + offset;

            point.geometry.coordinates = [newLng, originalLat];
        }

        seenCoordinates.add(coordinatesString);
        return point;
    });
};

export const sumChargerCounts = (chargers: any) => {
    return chargers.reduce((total: any, charger: any) => {
        // Check if "count" exists and is a number
        if (charger.hasOwnProperty('count') && typeof charger.count === 'number') {
            return total + charger.count;
        }
        return total;
    }, 0);
}

const AVAILABLE_STATUS_CODES = new Set(["ava", "aun", "ain", "aui", "are", "aur", "air", "auir"]);
const BUSY_STATUS_CODES = new Set(["inu", "uin", "res", "ure"]);

const normalizeStatus = (availability: unknown) => {
    const value = String(availability ?? "").trim().toLowerCase();

    if (AVAILABLE_STATUS_CODES.has(value)) {
        return "available";
    }

    if (BUSY_STATUS_CODES.has(value)) {
        return "busy";
    }

    return "default";
};

const normalizeConnectorType = (value: unknown) => {
    const normalized = String(value ?? "").trim().toLowerCase();

    if (normalized === "11" || normalized.includes("type 2") || normalized.includes("typetwo")) {
        return "Type 2";
    }

    if (normalized === "17" || normalized.includes("ccs")) {
        return "CCS";
    }

    if (normalized === "18" || normalized.includes("chademo")) {
        return "CHAdeMO";
    }

    return String(value ?? "Type 2");
};

const getCoordinates = (item: any): [number, number] => {
    const longitude = Number(
        item?.longitude ??
        item?.lng ??
        item?.long ??
        item?.lon ??
        item?.centerLongitude ??
        item?.centerLng ??
        item?.center?.lng ??
        item?.coordinates?.[0]
    );
    const latitude = Number(
        item?.latitude ??
        item?.lat ??
        item?.centerLatitude ??
        item?.centerLat ??
        item?.center?.lat ??
        item?.coordinates?.[1]
    );

    return [longitude, latitude];
};

const normalizeChargers = (item: any) => {
    const connectors = item?.connectors ?? item?.chargePoints ?? item?.plugs ?? [];

    return connectors.map((connector: any) => ({
        status: normalizeStatus(connector?.availability ?? connector?.status),
        type: normalizeConnectorType(connector?.connectorType ?? connector?.connector ?? connector?.type ?? connector?.plugType),
        count: Number(connector?.count ?? connector?.total ?? 1),
    }));
};

const getLocationLabel = (item: any) =>
    item?.address ??
    item?.location ??
    item?.addressLine ??
    item?.city ??
    item?.name ??
    "";

const getStationType = (item: any) =>
    item?.stationType ??
    item?.type ??
    item?.locationType ??
    "În parcare";

const getPointCount = (item: any) =>
    Number(item?.count ?? item?.pointCount ?? item?.clusterSize ?? item?.total ?? 0);

const isClusterItem = (item: any) =>
    String(item?.markerType ?? item?.type ?? "").toLowerCase() === "shard";

const normalizeCollection = (payload: any): any[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.results)) return payload.results;
    if (payload?.data && typeof payload.data === "object") return [payload.data];
    if (payload && typeof payload === "object") return [payload];
    return [];
};

const isChargePointDetailsItem = (item: any) =>
    item?.chargePoint && item?.chargePointExtra && Array.isArray(item?.chargePointPlugs);

const getChargePointDetailsCoordinates = (item: any): [number, number] => {
    const longitude = Number(
        item?.chargePoint?.longitude ??
        item?.chargePointExtra?.geoChargingPointEntrance?.longitude
    );
    const latitude = Number(
        item?.chargePoint?.latitude ??
        item?.chargePointExtra?.geoChargingPointEntrance?.latitude
    );

    return [longitude, latitude];
};

const normalizeDetailChargers = (plugs: any[] = []) => {
    const chargerCounts = new Map<string, {type: string; status: string; count: number}>();

    plugs.forEach((plug) => {
        const type = normalizeConnectorType(plug?.plugType);
        const status = normalizeStatus(plug?.status);
        const key = `${type}:${status}`;
        const currentValue = chargerCounts.get(key);

        if (currentValue) {
            currentValue.count += 1;
            return;
        }

        chargerCounts.set(key, {type, status, count: 1});
    });

    return Array.from(chargerCounts.values());
};

const dedupeChargePointPlugs = (plugs: any[] = []) => {
    const seenPlugs = new Set<string>();

    return plugs.filter((plug) => {
        const key = String(
            plug?.externalId ??
            plug?.chargePointId ??
            `${plug?.plugType ?? "unknown"}-${plug?.status ?? "unknown"}`
        );

        if (seenPlugs.has(key)) {
            return false;
        }

        seenPlugs.add(key);
        return true;
    });
};

const formatPricingValue = (value: unknown, currency: unknown, referenceUnit: unknown) => {
    if (typeof value !== "number" || !Number.isFinite(value)) {
        return null;
    }

    const normalizedCurrency = String(currency ?? "RON").trim() || "RON";
    const normalizedReferenceUnit = String(referenceUnit ?? "kWh").trim() || "kWh";

    return `${(value / 100).toFixed(2)} ${normalizedCurrency}/${normalizedReferenceUnit}`;
};

const buildPricingInfo = (plugs: any[] = []) => {
    const pricingByConnector = new Map<string, {
        connectorType: string;
        memberPrice: string | null;
        visitorPrice: string | null;
        subscriptionPrice: string | null;
        count: number;
    }>();

    plugs.forEach((plug) => {
        const pricingProduct = plug?.chargePointPricingProduct;
        const connectorType = normalizeConnectorType(plug?.plugType);

        const pricingEntry = {
            connectorType,
            memberPrice: formatPricingValue(
                pricingProduct?.value,
                pricingProduct?.currency,
                pricingProduct?.referenceUnit
            ),
            visitorPrice: formatPricingValue(
                pricingProduct?.guestValue,
                pricingProduct?.currency,
                pricingProduct?.referenceUnit
            ),
            subscriptionPrice: formatPricingValue(
                pricingProduct?.value,
                pricingProduct?.currency,
                pricingProduct?.referenceUnit
            ),
            count: 1,
        };

        const existingEntry = pricingByConnector.get(connectorType);

        if (existingEntry) {
            existingEntry.count += 1;
            return;
        }

        pricingByConnector.set(connectorType, pricingEntry);
    });

    return Array.from(pricingByConnector.values());
};

const getDetailLocationLabel = (item: any) => {
    const street = item?.chargePointExtra?.street;
    const city = item?.chargePointExtra?.city;
    const postalCode = item?.chargePointExtra?.postalCode;

    return [postalCode, city].filter(Boolean).join(", ");
};

const WEEKDAY_LABELS: Record<number, string> = {
    1: "Luni",
    2: "Marți",
    3: "Miercuri",
    4: "Joi",
    5: "Vineri",
    6: "Sâmbătă",
    7: "Duminică",
};

const getOpeningHoursInfo = (openingTimes: any): OpeningHoursInfo | null => {
    if (!openingTimes || typeof openingTimes !== "object") {
        return null;
    }

    if (openingTimes.twentyfourseven === true) {
        return { is24_7: true, days: [] };
    }

    const regularHours = Array.isArray(openingTimes.regular_hours)
        ? openingTimes.regular_hours
        : [];

    const intervalsByWeekday = new Map<number, string[]>();

    regularHours.forEach((entry: any) => {
        const weekday = Number(entry?.weekday);

        if (!Number.isInteger(weekday) || weekday < 1 || weekday > 7) {
            return;
        }

        const periodBegin = entry?.period_begin;
        const periodEnd = entry?.period_end;

        if (!periodBegin || !periodEnd) {
            return;
        }

        const intervals = intervalsByWeekday.get(weekday) ?? [];
        intervals.push(`${periodBegin} - ${periodEnd}`);
        intervalsByWeekday.set(weekday, intervals);
    });

    const days: OpeningHoursDay[] = [];

    for (let weekday = 1; weekday <= 7; weekday += 1) {
        const intervals = intervalsByWeekday.get(weekday);

        days.push({
            label: WEEKDAY_LABELS[weekday],
            hours: intervals && intervals.length > 0 ? intervals.join(", ") : "Închis",
        });
    }

    return { is24_7: false, days };
};

const transformChargePointDetails = (payload: any): StationPointFeature[] => {
    const items = normalizeCollection(payload).filter(isChargePointDetailsItem);

    if (items.length === 0) {
        return [];
    }

    const representativeItem = items[0];
    const [longitude, latitude] = getChargePointDetailsCoordinates(representativeItem);

    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
        return [];
    }

    const chargeStationId =
        representativeItem?.chargePoint?.chargeStationId ??
        representativeItem?.chargePoint?.id;

    const allPlugs = dedupeChargePointPlugs(
        items.flatMap((item: any) => item?.chargePointPlugs ?? [])
    );
    const primaryStatus =
        items.find((item: any) => normalizeStatus(item?.chargePoint?.availability) === "available")
            ?.chargePoint?.availability ??
        representativeItem?.chargePoint?.availability;

    return [
        {
            type: 'Feature' as const,
            properties: {
                id: chargeStationId,
                cluster: false,
                point_count: undefined,
                    data: {
                        id: String(chargeStationId),
                        name:
                            representativeItem?.chargePointExtra?.street ??
                            representativeItem?.chargePointExtra?.name ??
                            `Stație ${chargeStationId}`,
                        location: getDetailLocationLabel(representativeItem),
                        status: normalizeStatus(primaryStatus),
                        type: "În parcare",
                        chargers: normalizeDetailChargers(allPlugs),
                        openingHours: getOpeningHoursInfo(representativeItem?.chargePoint?.openingTimes),
                    pricing: buildPricingInfo(allPlugs),
                },
            },
            geometry: {
                type: 'Point' as const,
                coordinates: [longitude, latitude],
            },
        },
    ];
};

export const transformStations = (payload: any): StationPointFeature[] => {
    const normalizedPayload = normalizeCollection(payload);

    if (normalizedPayload.some(isChargePointDetailsItem)) {
        return transformChargePointDetails(payload);
    }

    return normalizedPayload
        .map((item: any): StationPointFeature | null => {
            const [longitude, latitude] = getCoordinates(item);

            if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
                return null;
            }

            const cluster = isClusterItem(item);
            const id = item?.id ?? item?.chargePointId ?? item?.clusterId ?? `${longitude}-${latitude}`;

            return {
                type: 'Feature' as const,
                properties: {
                    id,
                    cluster,
                    point_count: cluster ? getPointCount(item) : undefined,
                    data: cluster ? null : {
                        id: String(id),
                        name: item?.name ?? item?.stationName ?? `Stație ${id}`,
                        location: getLocationLabel(item),
                        status: normalizeStatus(item?.availability ?? item?.status),
                        type: getStationType(item),
                        chargers: normalizeChargers(item),
                    },
                },
                geometry: {
                    type: 'Point' as const,
                    coordinates: [longitude, latitude],
                },
            };
        })
        .filter(
            (
                item
            ): item is StationPointFeature => item !== null
        );
}

export const MAP_STYLES = [
    {
        "featureType": "administrative",
        "elementType": "labels.text.fill",
        "stylers": [
            {
                "color": "#444444"
            }
        ]
    },
    {
        "featureType": "landscape",
        "elementType": "all",
        "stylers": [
            {
                "color": "#f2f2f2"
            }
        ]
    },
    {
        "featureType": "poi",
        "elementType": "all",
        "stylers": [
            {
                "visibility": "off"
            }
        ]
    },
    {
        "featureType": "road",
        "elementType": "all",
        "stylers": [
            {
                "saturation": -100
            },
            {
                "lightness": 45
            }
        ]
    },
    {
        "featureType": "road.highway",
        "elementType": "all",
        "stylers": [
            {
                "visibility": "simplified"
            }
        ]
    },
    {
        "featureType": "road.arterial",
        "elementType": "labels.icon",
        "stylers": [
            {
                "visibility": "off"
            }
        ]
    },
    {
        "featureType": "transit",
        "elementType": "all",
        "stylers": [
            {
                "visibility": "off"
            }
        ]
    },
    {
        "featureType": "water",
        "elementType": "all",
        "stylers": [
            {
                "color": "#46bcec"
            },
            {
                "visibility": "on"
            }
        ]
    }
];
